// src/colaboratori.js
import * as React from "react";
import { useState } from "react";
import {
    Create, Edit, SimpleForm, TextInput,
    List, Datagrid, TextField, FunctionField,
    Toolbar, SaveButton, TopToolbar, CreateButton,
    SimpleList, useNotify, useRecordContext,
    required, email as emailValidation,
} from "react-admin";
import { RolTagsInput } from "./RolTagsInput";
import { useCanManage } from "./usePermissions";
import { getApiErrorMessage } from "./apiError";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MuiTextField from "@mui/material/TextField";
import { useDataProvider } from "react-admin";
import { useNavigate } from "react-router-dom";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

// Toolbar fara buton de salvare - folosit cand userul nu are voie sa editeze.
// Formularul ramane vizibil (read-only via inputuri disabled), dar nu se
// poate trimite nimic la server.
const ReadOnlyToolbar = () => <Toolbar sx={{ display: "none" }} />;

const SaveOnlyToolbar = () => (
    <Toolbar>
        <SaveButton />
    </Toolbar>
);

// Dialog pentru schimbarea parolei - trimite PATCH doar cu campul password,
// evitand trimiterea unui string gol care ar reseta parola accidental.
const SchimbaParolaDialog = ({ open, onClose, colaboratorId }) => {
    const [parola, setParola] = useState("");
    const [confirmare, setConfirmare] = useState("");
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const handleSave = async () => {
        if (!parola) { notify("Introdu o parola", { type: "warning" }); return; }
        if (parola !== confirmare) { notify("Parolele nu coincid", { type: "warning" }); return; }
        if (parola.length < 6) { notify("Parola trebuie sa aiba cel putin 6 caractere", { type: "warning" }); return; }
        try {
            await dataProvider.update("colaboratori", {
                id: colaboratorId,
                data: { password: parola },
                previousData: {},
            });
            notify("Parola a fost schimbata", { type: "success" });
            setParola("");
            setConfirmare("");
            onClose();
        } catch (err) {
            notify("Eroare la schimbarea parolei: " + (err.message || ""), { type: "error" });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Schimba parola</DialogTitle>
            <DialogContent>
                <MuiTextField
                    autoFocus fullWidth type="password" label="Parola noua"
                    value={parola} onChange={(e) => setParola(e.target.value)}
                    sx={{ mt: 1, mb: 2 }}
                />
                <MuiTextField
                    fullWidth type="password" label="Confirma parola"
                    value={confirmare} onChange={(e) => setConfirmare(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Anuleaza</Button>
                <Button variant="contained" onClick={handleSave}>Salveaza</Button>
            </DialogActions>
        </Dialog>
    );
};

const SaveToolbarCuParola = () => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const record = useRecordContext();

    return (
        <>
            <Toolbar sx={{ justifyContent: "space-between" }}>
                <SaveButton />
                <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => setDialogOpen(true)}
                    sx={{ ml: 2 }}
                >
                    Schimba parola
                </Button>
            </Toolbar>
            <SchimbaParolaDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                colaboratorId={record?.id}
            />
        </>
    );
};

export const ColaboratorCreate = () => {
    const notify = useNotify();
    return (
        <Create mutationOptions={{ onError: (err) => {
            // Erorile pe campuri sunt deja afisate sub input-uri de React Admin;
            // notificam doar erorile generale (403, 500, retea etc.)
            if (err?.body?.errors && Object.keys(err.body.errors).length) return;
            notify(getApiErrorMessage(err), { type: "error" });
        } }}>
            <SimpleForm>
                <TextInput source="nume" validate={required()} />
                <TextInput source="prenume" validate={required()} />
                <TextInput source="telefon" validate={required()} />
                <TextInput source="email" validate={[required(), emailValidation()]} />
                <RolTagsInput source="rol" />
                <TextInput source="peOra" label="Tarif/oră net (lei)" type="number" helperText="Vizibil doar pentru admin" />
            </SimpleForm>
        </Create>
    );
};

const ColaboratorEditForm = ({ canManage }) => {
    const record = useRecordContext();
    const navigate = useNavigate();
    const myId = parseInt(localStorage.getItem('colaborator_id'), 10);
    const isOwnProfile = record?.id === myId;

    return (
        <SimpleForm toolbar={canManage && !isOwnProfile ? <SaveToolbarCuParola /> : <ReadOnlyToolbar />}>
            {isOwnProfile && (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/profil')}
                    sx={{ mb: 2 }}
                    startIcon={<AccountCircleIcon />}
                >
                    Editează profilul meu
                </Button>
            )}
            <TextInput source="nume" disabled />
            <TextInput source="prenume" disabled />
            <TextInput source="telefon" disabled={!canManage || isOwnProfile} helperText={!canManage ? "Doar adminii pot edita" : undefined} />
            <TextInput source="email" disabled={!canManage || isOwnProfile} />
            <RolTagsInput source="rol" disabled={!canManage || isOwnProfile} />
            {canManage && !isOwnProfile && <TextInput source="peOra" label="Tarif/oră net (lei)" type="number" helperText="Vizibil doar pentru admin" />}
        </SimpleForm>
    );
};

export const ColaboratorEdit = () => {
    const canManage = useCanManage();
    const notify = useNotify();

    return (
        <Edit mutationOptions={{ onError: (err) => notify(getApiErrorMessage(err), { type: "error" }) }}>
            <ColaboratorEditForm canManage={canManage} />
        </Edit>
    );
};

// Buton "Create" vizibil doar pentru admin/superadmin
const ColaboratorListActions = () => {
    const canManage = useCanManage();
    if (!canManage) return <TopToolbar />;
    return (
        <TopToolbar>
            <CreateButton />
        </TopToolbar>
    );
};

export const ColaboratorList = (props) => {
    const canManage = useCanManage();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const myId = parseInt(localStorage.getItem('colaborator_id'), 10);
    const dataProvider = useDataProvider();
    const [echipaMap, setEchipaMap] = React.useState({});

    React.useEffect(() => {
        dataProvider.getList("echipe", { pagination: { page: 1, perPage: 500 }, sort: { field: "id", order: "ASC" }, filter: {} })
            .then(({ data }) => {
                const map = {};
                data.forEach((e) => { map[e.id] = e.nume; });
                setEchipaMap(map);
            })
            .catch(() => {});
    }, [dataProvider]);

    const numeEchipa = (record) => {
        if (!record.echipa) return <span style={{ opacity: 0.4 }}>—</span>;
        const id = typeof record.echipa === 'string'
            ? parseInt(record.echipa.match(/\/(\d+)$/)?.[1], 10)
            : record.echipa?.id ?? record.echipa;
        return echipaMap[id] ?? <span style={{ opacity: 0.4 }}>—</span>;
    };

    return (
        <List {...props} actions={<ColaboratorListActions />}>
            {isMobile ? (
                <SimpleList
                    primaryText={(record) => `${record.nume} ${record.prenume}`}
                    secondaryText={(record) => record.rol}
                    tertiaryText={(record) => numeEchipa(record)}
                    linkType="edit"
                />
            ) : (
                <Datagrid
                    rowClick="edit"
                    bulkActionButtons={canManage}
                    rowSx={(record) => {
                        if (record.superAdmin) return { backgroundColor: "#e65100", color: "#fff" };
                        if (record.rol?.includes('admin')) return { backgroundColor: "#f57f17", color: "#fff" };
                        return {};
                    }}
                >
                    <TextField source="id" />
                    <TextField source="nume" />
                    <TextField source="prenume" />
                    <FunctionField
                        label="Telefon"
                        render={(record) => record.telefon ?? <span style={{ opacity: 0.4 }}>—</span>}
                    />
                    <FunctionField
                        label="Email"
                        render={(record) => record.email ?? <span style={{ opacity: 0.4 }}>—</span>}
                    />
                    <FunctionField
                        label="Echipă"
                        render={(record) => numeEchipa(record)}
                    />
                    <FunctionField
                        label="Roluri"
                        render={(record) => {
                            const roluri = record.superAdmin ? ["superadmin", ...(record.rol ? record.rol.split(",") : [])] : (record.rol ? record.rol.split(",") : []);
                            return roluri.join(", ") || "—";
                        }}
                    />
                    <FunctionField
                        label="Tarif/oră (lei)"
                        render={(record) => record.peOra != null ? `${record.peOra} lei` : <span style={{ opacity: 0.4 }}>—</span>}
                    />
                </Datagrid>
            )}
        </List>
    );
};
